// packages/shared/src/roleforge/methodology/roi-calculator.ts
// Plan 3b-i: Money Slide formulas, with honest-framing data shape.
// LOCKED rules (§8): cost_savings = hard floor; revenue_uplift = soft ceiling
// (italic/lighter, marked "potential"); Year-1 ROI shown both ways.

import type { RoiInputRow, RoiBreakdown, Year1Roi } from './types';

export const HOURS_PER_YEAR = 52;
export const HOURS_PER_FTE_PER_YEAR = 2080;
export const DEFAULT_REALLOCATION_RATE = 0.5;

export function deriveHourlyRateFromSalary(annual_salary_cents: number): number {
  return Math.round(annual_salary_cents / HOURS_PER_FTE_PER_YEAR);
}

export interface ComputeRoiOptions {
  /**
   * Aggregate implementation cost across the program — overrides per-row sum
   * if provided. Use when the methodology agent has a holistic estimate.
   */
  implementation_cost_cents_override?: number;
  /**
   * Note text shown next to the revenue_uplift figure.
   * Default explains the assumption per §8.
   */
  revenue_uplift_note?: string;
}

export function computeRoi(
  rows: RoiInputRow[],
  options: ComputeRoiOptions = {},
): RoiBreakdown {
  let hours_saved_per_week = 0;
  let cost_savings_annual_cents = 0;
  let revenue_uplift_annual_cents = 0;
  const per_row: NonNullable<RoiBreakdown['per_row']> = [];

  for (const r of rows) {
    const hsw = r.employees * r.hours_per_week * r.percent_saved_by_ai;
    const row_cost_savings = Math.round(hsw * r.hourly_rate_cents * HOURS_PER_YEAR);
    const row_revenue_uplift = Math.round(
      hsw * r.reallocation_rate * r.value_per_hour_cents * HOURS_PER_YEAR,
    );
    hours_saved_per_week += hsw;
    cost_savings_annual_cents += row_cost_savings;
    revenue_uplift_annual_cents += row_revenue_uplift;
    per_row.push({
      row_id: r.id,
      label: r.label,
      cost_savings_annual_cents: row_cost_savings,
      revenue_uplift_annual_cents: row_revenue_uplift,
      fte_equivalent: (hsw * HOURS_PER_YEAR) / HOURS_PER_FTE_PER_YEAR,
    });
  }

  const total_implementation_cost_cents = options.implementation_cost_cents_override
    ?? rows.reduce((sum, r) => sum + r.implementation_cost_cents, 0);

  // Issue #1205: implementation_cost_cents defaults to 0 in the DB. Dividing by a
  // clamped 1-cent denominator produced Year-1 ROI figures in the hundreds of
  // millions of percent on a paid deliverable. With no captured cost there is no
  // denominator and therefore no number we can stand behind — say so instead.
  const year_1_roi: Year1Roi = total_implementation_cost_cents > 0
    ? {
        available: true,
        cost_savings_only_pct:
          (cost_savings_annual_cents / total_implementation_cost_cents) * 100,
        combined_pct:
          ((cost_savings_annual_cents + revenue_uplift_annual_cents)
            / total_implementation_cost_cents) * 100,
      }
    : { available: false, reason: 'implementation_cost_not_captured' };

  const default_note = `If ${Math.round(DEFAULT_REALLOCATION_RATE * 100)}% of saved hours are reallocated to revenue-generating activity at the value-per-hour you provided.`;

  return {
    hours_saved_per_week,
    cost_savings: {
      annual_cents: cost_savings_annual_cents,
      display_treatment: 'hard_floor',
    },
    revenue_uplift: {
      annual_cents: revenue_uplift_annual_cents,
      display_treatment: 'soft_ceiling',
      note: options.revenue_uplift_note ?? default_note,
    },
    total_implementation_cost_cents,
    year_1_roi,
    per_row,
  };
}

// ---------------------------------------------------------------------------
// Display helpers — the ONLY sanctioned way to turn a Year1Roi into text.
// Keeping these here means every render site (app, PDF export, email) prints
// the same honest "not enough data" state instead of inventing a number.
// ---------------------------------------------------------------------------

/** Short label shown in place of a percentage when ROI cannot be computed. */
export const YEAR_1_ROI_UNAVAILABLE_LABEL = 'Not enough data';

/** One-line explanation of what the owner needs to supply. */
export const YEAR_1_ROI_UNAVAILABLE_EXPLANATION =
  'Year-1 ROI needs an implementation cost. Add the cost to your ROI inputs and this figure appears.';

/**
 * Format one side of the Year-1 ROI. Returns the "not enough data" label rather
 * than a percentage whenever implementation cost was never captured.
 */
export function formatYear1RoiPct(
  roi: Year1Roi,
  which: 'cost_savings_only' | 'combined',
): string {
  if (!roi.available) return YEAR_1_ROI_UNAVAILABLE_LABEL;
  const pct = which === 'combined' ? roi.combined_pct : roi.cost_savings_only_pct;
  return `${Math.round(pct)}%`;
}

/**
 * Helper for the React UI: format an ROI breakdown row for table display.
 * The honest-framing rule means cost-savings always renders bold/solid; revenue-uplift
 * always renders italic/lighter with the assumption note.
 */
export function formatRoiTableRow(row: NonNullable<RoiBreakdown['per_row']>[number]) {
  return {
    label: row.label,
    fte_equivalent: row.fte_equivalent.toFixed(2),
    cost_savings_display: {
      value_cents: row.cost_savings_annual_cents,
      treatment: 'bold' as const,
    },
    revenue_uplift_display: {
      value_cents: row.revenue_uplift_annual_cents,
      treatment: 'italic' as const,
      label_suffix: ' (potential)',
    },
  };
}
